import {
  Box, Flex, Text, Title, Button
} from '@huspy/briks-web';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import { useTranslation } from 'react-i18next';
import {
  Image, SimpleGrid, Skeleton
} from '@mantine/core';
import { imagesPath } from '@shared/css.const';
import { useGetProducts } from '@modules/payments/hooks/queries/useGetProducts';
import { useCreateCheckoutSession } from '@modules/payments/hooks/mutations/useCreateCheckoutSession';
import Loader from '@shared/loader';
import {
  useState, useEffect, useMemo
} from 'react';
import { CURRENCY_UNITS } from '@modules/payments/const';
import { bundleStyles, creditStyles } from '@modules/payments/presentation/features/BuyCreditsSlido/styles';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import useCredits from '@modules/core/hooks/useCredits';
import { useGlobalStore } from '@modules/core/store';
import usePersistUser from '@modules/core/hooks/usePersistUser';
import { USER_ROLE } from '@modules/identity/api/const';
import { Lock } from '@huspy/briks-icons';
import { useLocation } from '@tanstack/react-router';
import { slidoDeepLink } from '@modules/core/utils';

const getCreditsImage = (credits: number): string => {
  if (credits >= 150) return `${imagesPath}/credits-quantity-xl.png`;
  if (credits >= 80) return `${imagesPath}/credits-quantity-lg.png`;
  return `${imagesPath}/credits-quantity-sm.png`;
};

type CreditBundleProps = {
  product_id: string;
  amount: number;
  currency: string;
  credits: number;
  imageUrl: string | null;
  description: string | null;
  name: string;
};

const CreditBundle = (bundle: CreditBundleProps) => {
  const { t } = useTranslation();
  const {
    product_id, credits, currency, amount, imageUrl, description, name,
  } = bundle;
  const { mutateAsync } = useCreateCheckoutSession();
  const [loading, setLoading] = useState(false);
  const quantity = 1;

  const onClickBuy = async () => {
    trackAmplitudeEvent(
      USER_EVENTS.CREDITS.PURCHASE_ATTEMPTED,
      {
        useContext: true,
        credit_price: amount,
        credit_quantity: credits,
        credit_bundle: name,
        credit_bundle_quantity: quantity,
      }
    );
    setLoading(true);

    const baseUrl = window.location.origin + window.location.pathname;
    const savedSearch = window.location.search.replace('?', '');
    const result = await mutateAsync({
      products: [{ product_id, quantity }],
      success_url: `${baseUrl}?payment_flow=true&message=success&credits=${credits}&${savedSearch}`,
      // We don't want to show the "cancel" slido but we want to keep it.
      // Having any other screen as message won't show the slido. For now
      // we will name the message as "user_cancel", which might be beneficial
      // for analytics
      cancel_url: `${baseUrl}?payment_flow=true&message=user_cancel&credits=${credits}&${savedSearch}`,
    });

    if (result.url) {
      window.location.href = result.url;
    } else {
      setLoading(false);
    }
  };

  return (
    <Box className={ bundleStyles.container }>
      <Flex gap={ 1 } justify='center' align='center' className={ bundleStyles.creditsContainer }>
        <Image w={ 30 } src={ imageUrl ?? getCreditsImage(credits) } />
        <Text size='3xl' weight='semiBold'>{credits}</Text>
      </Flex>

      <Flex mt='8px' gap='10px' direction='column' align='flex-start'>
        <Text size='md' weight='regular' color='text-tertiary'>
          {description || t('payments.buySlido.creditBundles.bundleDescription', { credits })}
        </Text>
        <Text size='lg' weight='medium' data-testid={ `product-${product_id}-amount` }>
          {currency}
          {' '}
          {amount}
        </Text>
        <Button
          disabled={ loading }
          onClick={ onClickBuy }
        >
          { loading
            ? (
              <>
                <Loader />
                {t('payments.buySlido.creditBundles.buyNowLoading')}
              </>
            )
            : t('payments.buySlido.creditBundles.buyNow')}

        </Button>
      </Flex>
    </Box>
  );
};

const BuyCreditsContent = ({ creditsToSpend }: { creditsToSpend?: number }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetProducts();
  const { data: creditsData } = useCredits();
  const { user } = usePersistUser();

  const isUserManager = user?.roles?.includes(USER_ROLE.manager);

  return (
    <>
      <Flex justify='center' className={ creditStyles.container }>
        <Flex direction='column' alignContent='center' gap={ 3 } textAlign='center'>
          <Flex direction='column' align='center'>
            <Text size='2xl' color='text-primary_on-brand'>{t('core.credits.currentCredits')}</Text>

            <Flex className={ creditStyles.availableCreditsContainer }>
              <Title size='h2' weight='semiBold' color='text-primary_on-brand' data-testid='available-credits'>
                {creditsData?.availableCredits ?? '??'}
              </Title>

              <Box className={ creditStyles.creditsIconContainer }>
                <Image w={ 32 } src={ `${imagesPath}/credits.png` } />
              </Box>
            </Flex>
          </Flex>

          {creditsToSpend ? (
            <Text size='md' color='text-primary_on-brand' data-testid='credits-required'>
              Credits required for this action:
              {' '}
              { Math.abs(creditsToSpend) }
            </Text>
          ) : null}
        </Flex>
      </Flex>

      <Box p={ 6 }>
        {isUserManager ? (
          <>
            <Flex direction='column'>
              <Text size='3xl' weight='medium'>{t('payments.buySlido.creditBundles.title')}</Text>
              <Text size='md' weight='regular' color='text-tertiary'>
                {t('payments.buySlido.creditBundles.description')}
              </Text>
            </Flex>

            <SimpleGrid pt={ 20 } cols={ 3 } spacing='16'>
              { isLoading || !data
                ? (
                  <>
                    <Skeleton w='100%' h='222px' radius='10px' />
                    <Skeleton w='100%' h='222px' radius='10px' />
                    <Skeleton w='100%' h='222px' radius='10px' />
                  </>
                )
                : data
                  .products
                  .filter((bundle) => !!bundle.metadata?.credits)
                  .sort((a, b) => a.metadata.credits - b.metadata.credits)
                  .map((bundle) => (
                    <CreditBundle
                      key={ bundle.id }
                      product_id={ bundle.id }
                      amount={ bundle.price_in_cents / CURRENCY_UNITS.CENTS }
                      currency={ bundle.currency }
                      credits={ bundle.metadata.credits }
                      imageUrl={ bundle.image_url }
                      description={ bundle.description }
                      name={ bundle.name }
                    />
                  ))}
            </SimpleGrid>
          </>
        ) : (
          <Flex direction='column' gap={ 3 }>
            <Lock width='32px' height='32px' />
            <Flex direction='column'>
              <Text size='lg'>
                {t('core.credits.dontHaveEnoughCredits')}
              </Text>
              <Text size='md' color='text-secondary'>
                {t('core.credits.contactManager')}
                {' '}
                {t('core.credits.toTopUpCredits')}
                .
              </Text>
            </Flex>
          </Flex>
        )}
      </Box>
    </>
  );
};

export const BuyCreditsSlido = () => {
  const { t } = useTranslation();
  const { credits, setIsOpenedBuyCredits, setBuyCreditsToSpend } = useGlobalStore();
  const { data: creditsData, isLoading: isCreditsLoading, isEnoughCredits } = useCredits();
  const { hash } = useLocation();

  const isLowCredits = useMemo(() => {
    if (!isCreditsLoading && creditsData) {
      return !isEnoughCredits(credits.creditsToSpend ?? 0);
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreditsLoading, creditsData, credits]);

  const close = () => {
    setIsOpenedBuyCredits(false);
    setBuyCreditsToSpend(0);
  };

  useEffect(() => {
    if (credits.isOpenedBuyCredits) {
      trackAmplitudeEvent(USER_EVENTS.CREDITS.PURCHASE_PAGE_VIEWED);
    }
  }, [credits.isOpenedBuyCredits]);

  useEffect(() => {
    if (slidoDeepLink.BUY_CREDIT === hash) {
      setIsOpenedBuyCredits(true);
    }
  }, [setIsOpenedBuyCredits, hash]);

  return (
    <BaseSlido
      opened={ credits.isOpenedBuyCredits }
      inTopOfAll
      close={ close }
      title={ (
        <Flex direction='column' gap='2' p='6' pb='0' data-testid='buy-credits-title'>
          <Text size='5xl'>
            { isLowCredits ? t('payments.buySlido.lowCredits') : t('payments.buySlido.title') }
          </Text>
          <Text size='md' color='neutral.500'>
            { isLowCredits
              ? t('payments.buySlido.lowCreditsSubtitle')
              : t('payments.buySlido.description') }
          </Text>
        </Flex>
      ) }
    >
      <BuyCreditsContent creditsToSpend={ isLowCredits ? credits.creditsToSpend : undefined } />
    </BaseSlido>
  );
};
